import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Tag, Save, RefreshCcw, AlertCircle, CheckCircle2, Loader2, Package, FileText, Code,
  Plus, Trash2, X, Copy,
} from 'lucide-react';
import { adminApi } from './AdminAuthContext';

/**
 * Admin price editor.
 *
 * The full prices.json document is the unit of edit. We keep a local
 * working copy in component state; clicking "Speichern" PUTs the whole
 * object back to the backend. Four tabs let the user focus:
 *   • Einzelpreise — per-product base/sale numbers per category (add/remove)
 *   • Bundles     — name + discount % + product mix (add/remove)
 *   • Hinweise    — disclaimer strings NL + DE
 *   • Raw JSON    — power-user fallback with on-save validation
 */

const TABS = [
  { id: 'products', label: 'Einzelpreise', Icon: Tag },
  { id: 'bundles',  label: 'Bundles',      Icon: Package },
  { id: 'labels',   label: 'Hinweise',     Icon: FileText },
  { id: 'raw',      label: 'Raw JSON',     Icon: Code },
];

const BADGE_OPTIONS = [
  { value: '',            label: '— Kein Badge —' },
  { value: 'new',         label: 'new (grün)' },
  { value: 'bestseller',  label: 'bestseller (orange)' },
  { value: 'limited',     label: 'limited (gold)' },
  { value: 'recommended', label: 'recommended (schwarz)' },
];

const SLUG_RE = /^[a-z0-9][a-z0-9-]{0,40}$/;

function isPriceObject(v) {
  return v != null && typeof v === 'object' && !Array.isArray(v);
}

function toNumberOrNull(v) {
  if (v === '' || v == null) return null;
  const n = Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

function deepClone(d) { return JSON.parse(JSON.stringify(d)); }


// ============================================================================
// Products tab — add/remove categories + products + edit base/sale/badge
// ============================================================================

function AddProductRow({ onAdd, testId }) {
  const [slug, setSlug] = useState('');
  const [price, setPrice] = useState('');
  const [error, setError] = useState(null);

  const submit = () => {
    setError(null);
    const s = slug.trim().toLowerCase();
    if (!SLUG_RE.test(s)) { setError('Slug: a-z, 0-9, Bindestrich, 1-40 Zeichen.'); return; }
    const p = toNumberOrNull(price);
    if (p == null || p < 0) { setError('Preis muss eine Zahl ≥ 0 sein.'); return; }
    onAdd(s, p);
    setSlug(''); setPrice('');
  };

  return (
    <div data-testid={testId} className="p-3 bg-[#FAF8F2] border-t border-[#E5E0D2]">
      <div className="flex flex-wrap items-center gap-2">
        <input type="text" value={slug} placeholder="produkt-slug" data-testid={`${testId}-slug`}
          onChange={(e) => setSlug(e.target.value)}
          className="flex-1 min-w-[140px] px-2 py-1 rounded border border-[#E5E0D2] text-[13px]" />
        <input type="number" value={price} placeholder="Preis €" data-testid={`${testId}-price`}
          onChange={(e) => setPrice(e.target.value)}
          className="w-28 px-2 py-1 rounded border border-[#E5E0D2] text-[13px] tabular-nums" />
        <button type="button" onClick={submit} data-testid={`${testId}-submit`}
          className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#1F1D1A] text-white text-[12px] font-semibold hover:bg-[#F26B1A] transition-colors">
          <Plus className="w-3.5 h-3.5" /> Produkt
        </button>
      </div>
      {error && <p className="mt-1.5 text-[11.5px] text-[#C03A1A] inline-flex items-center gap-1"><AlertCircle className="w-3 h-3" /> {error}</p>}
    </div>
  );
}

function AddCategoryForm({ onAdd, existing }) {
  const [name, setName] = useState('');
  const [error, setError] = useState(null);

  const submit = () => {
    setError(null);
    const s = name.trim().toLowerCase();
    if (!SLUG_RE.test(s)) { setError('Kategorie: a-z, 0-9, Bindestrich, 1-40 Zeichen.'); return; }
    if (existing.includes(s)) { setError(`Kategorie "${s}" existiert bereits.`); return; }
    onAdd(s);
    setName('');
  };

  return (
    <div data-testid="add-category-form" className="bg-white rounded-2xl border border-dashed border-[#E5E0D2] p-4">
      <p className="text-[11.5px] uppercase tracking-wider font-semibold text-[#5C5852] mb-2">
        Neue Kategorie anlegen
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <input type="text" value={name} placeholder="z. B. rolgordijnen" data-testid="add-category-name"
          onChange={(e) => setName(e.target.value)}
          className="flex-1 min-w-[160px] px-2 py-1.5 rounded border border-[#E5E0D2] text-[13px]" />
        <button type="button" onClick={submit} data-testid="add-category-submit"
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#F26B1A] text-white text-[12px] font-semibold hover:bg-[#D85A0A] transition-colors">
          <Plus className="w-3.5 h-3.5" /> Kategorie
        </button>
      </div>
      {error && <p className="mt-1.5 text-[11.5px] text-[#C03A1A] inline-flex items-center gap-1"><AlertCircle className="w-3 h-3" /> {error}</p>}
    </div>
  );
}

function ProductsTab({ data, onChange }) {
  const cats = data?.prices ? Object.keys(data.prices) : [];

  const updateProduct = (cat, prod, patch) => {
    const next = deepClone(data);
    const cur = next.prices[cat][prod];
    if (typeof cur === 'number') {
      next.prices[cat][prod] = { base: cur, ...patch };
    } else {
      next.prices[cat][prod] = { ...cur, ...patch };
    }
    onChange(next);
  };

  const addProduct = (cat, slug, price) => {
    if (data.prices[cat]?.[slug] !== undefined) {
      // already exists — silently ignore (form should not be reachable in this state)
      return;
    }
    const next = deepClone(data);
    next.prices[cat][slug] = { base: price };
    onChange(next);
  };

  const removeProduct = (cat, slug) => {
    if (!window.confirm(`Produkt "${slug}" aus Kategorie "${cat}" entfernen?`)) return;
    const next = deepClone(data);
    delete next.prices[cat][slug];
    onChange(next);
  };

  const addCategory = (name) => {
    const next = deepClone(data);
    next.prices[name] = {};
    onChange(next);
  };

  const removeCategory = (cat) => {
    const products = Object.keys(data.prices[cat] || {});
    if (products.length > 0) {
      if (!window.confirm(`Kategorie "${cat}" enthält ${products.length} Produkte. Wirklich komplett löschen?`)) return;
    } else if (!window.confirm(`Leere Kategorie "${cat}" löschen?`)) return;
    const next = deepClone(data);
    delete next.prices[cat];
    onChange(next);
  };

  return (
    <div className="space-y-6" data-testid="prices-tab-products">
      {cats.map((cat) => {
        const products = Object.entries(data.prices[cat]);
        return (
          <section key={cat} data-testid={`category-${cat}`} className="bg-white rounded-2xl border border-[#E5E0D2] overflow-hidden">
            <header className="px-5 py-3 bg-[#FAF8F2] border-b border-[#E5E0D2] flex items-center justify-between gap-2 flex-wrap">
              <p className="font-display text-[15px] text-[#1F1D1A] capitalize">{cat}</p>
              <button type="button" onClick={() => removeCategory(cat)}
                data-testid={`category-${cat}-delete`}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] text-[#5C5852] hover:bg-white hover:text-[#C03A1A] transition-colors">
                <Trash2 className="w-3 h-3" /> Kategorie löschen
              </button>
            </header>
            <div className="overflow-x-auto">
              <table className="w-full text-[13px] min-w-[640px]">
                <thead>
                  <tr className="text-left text-[10.5px] uppercase tracking-wider text-[#5C5852] border-b border-[#E5E0D2]">
                    <th className="p-3 font-semibold">Produkt</th>
                    <th className="p-3 font-semibold w-32">Basispreis (€)</th>
                    <th className="p-3 font-semibold w-32">Aktion (€)</th>
                    <th className="p-3 font-semibold w-36">Badge</th>
                    <th className="p-3 font-semibold w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {products.length === 0 ? (
                    <tr><td colSpan={5} className="p-4 text-[12px] text-[#5C5852] text-center">Noch keine Produkte in dieser Kategorie.</td></tr>
                  ) : products.map(([prod, val]) => {
                    const base  = isPriceObject(val) ? val.base : (typeof val === 'number' ? val : '');
                    const sale  = isPriceObject(val) ? (val.sale ?? '') : '';
                    const badge = isPriceObject(val) ? (val.badge ?? '') : '';
                    return (
                      <tr key={prod} className="border-b border-[#E5E0D2] last:border-b-0">
                        <td className="p-3 font-semibold">{prod}</td>
                        <td className="p-3">
                          <input type="number" step="1" value={base ?? ''} data-testid={`price-${cat}-${prod}-base`}
                            onChange={(e) => updateProduct(cat, prod, { base: toNumberOrNull(e.target.value) })}
                            className="w-full px-2 py-1 rounded border border-[#E5E0D2] text-[13px] tabular-nums" />
                        </td>
                        <td className="p-3">
                          <input type="number" step="1" value={sale} data-testid={`price-${cat}-${prod}-sale`}
                            placeholder="—"
                            onChange={(e) => {
                              const v = toNumberOrNull(e.target.value);
                              if (v == null) {
                                const next = deepClone(data);
                                const cur = next.prices[cat][prod];
                                if (isPriceObject(cur)) {
                                  const { sale: _drop, ...rest } = cur;
                                  next.prices[cat][prod] = rest;
                                  onChange(next);
                                }
                              } else {
                                updateProduct(cat, prod, { sale: v });
                              }
                            }}
                            className="w-full px-2 py-1 rounded border border-[#E5E0D2] text-[13px] tabular-nums" />
                        </td>
                        <td className="p-3">
                          <select value={badge} data-testid={`price-${cat}-${prod}-badge`}
                            onChange={(e) => updateProduct(cat, prod, { badge: e.target.value || undefined })}
                            className="w-full px-2 py-1 rounded border border-[#E5E0D2] text-[12px] bg-white">
                            {BADGE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                        </td>
                        <td className="p-3 text-right">
                          <button type="button" onClick={() => removeProduct(cat, prod)}
                            data-testid={`price-${cat}-${prod}-delete`}
                            title="Produkt entfernen"
                            className="inline-flex w-7 h-7 items-center justify-center rounded-full text-[#5C5852] hover:bg-[#C03A1A]/10 hover:text-[#C03A1A] transition-colors">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <AddProductRow testId={`add-product-${cat}`} onAdd={(slug, price) => addProduct(cat, slug, price)} />
          </section>
        );
      })}

      <AddCategoryForm existing={cats} onAdd={addCategory} />

      <p className="text-[12px] text-[#5C5852]">
        Tipp: Aktionspreis leer lassen → Produkt ist nicht im Sale.
        Slugs ohne Leerzeichen, nur a-z / 0-9 / Bindestriche.
      </p>
    </div>
  );
}


// ============================================================================
// Bundles tab — add/remove bundles + edit products inside bundle
// ============================================================================

function ProductPicker({ data, value, onChange, testId }) {
  // value: { category, slug, qty }
  const cats = Object.keys(data?.prices || {});
  const slugs = value?.category ? Object.keys(data.prices[value.category] || {}) : [];
  return (
    <div data-testid={testId} className="flex flex-wrap items-center gap-1.5">
      <select value={value.category || ''} data-testid={`${testId}-cat`}
        onChange={(e) => onChange({ ...value, category: e.target.value, slug: '' })}
        className="px-2 py-1 rounded border border-[#E5E0D2] text-[12px] bg-white">
        <option value="">— Kategorie —</option>
        {cats.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      <select value={value.slug || ''} data-testid={`${testId}-slug`}
        onChange={(e) => onChange({ ...value, slug: e.target.value })}
        disabled={!value.category}
        className="px-2 py-1 rounded border border-[#E5E0D2] text-[12px] bg-white disabled:opacity-50">
        <option value="">— Produkt —</option>
        {slugs.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <input type="number" min={1} max={99} value={value.qty || 1} data-testid={`${testId}-qty`}
        onChange={(e) => onChange({ ...value, qty: Math.max(1, Number(e.target.value) || 1) })}
        className="w-16 px-2 py-1 rounded border border-[#E5E0D2] text-[12px] tabular-nums" />
      <span className="text-[11px] text-[#5C5852]">Stück</span>
    </div>
  );
}

function AddBundleForm({ existingIds, onAdd }) {
  const [open, setOpen] = useState(false);
  const [id, setId] = useState('');
  const [nameDe, setNameDe] = useState('');
  const [nameNl, setNameNl] = useState('');
  const [error, setError] = useState(null);

  const submit = () => {
    setError(null);
    const s = id.trim().toLowerCase();
    if (!SLUG_RE.test(s)) { setError('Bundle-ID: a-z, 0-9, Bindestrich, 1-40 Zeichen.'); return; }
    if (existingIds.includes(s)) { setError(`Bundle-ID "${s}" existiert bereits.`); return; }
    if (!nameDe.trim() || !nameNl.trim()) { setError('Bitte beide Namen (DE + NL) ausfüllen.'); return; }
    onAdd({
      id: s,
      name: { de: nameDe.trim(), nl: nameNl.trim() },
      tagline: { de: '', nl: '' },
      products: [],
      discount: 10,
      highlight: false,
      soldOut: false,
      badge: 'new',
      saleEnds: null,
      stockLeft: null,
    });
    setId(''); setNameDe(''); setNameNl(''); setOpen(false);
  };

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} data-testid="add-bundle-open"
        className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-2xl bg-white border border-dashed border-[#E5E0D2] text-[13px] font-semibold text-[#5C5852] hover:border-[#F26B1A] hover:text-[#F26B1A] transition-colors">
        <Plus className="w-4 h-4" /> Neues Bundle anlegen
      </button>
    );
  }
  return (
    <div data-testid="add-bundle-form" className="bg-white rounded-2xl border-2 border-[#F26B1A]/40 p-5 space-y-3">
      <div className="flex items-center justify-between">
        <p className="font-display text-[16px]">Neues Bundle</p>
        <button type="button" onClick={() => { setOpen(false); setError(null); }}
          className="w-7 h-7 inline-flex items-center justify-center rounded-full text-[#5C5852] hover:bg-[#FAF8F2]">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <label className="block">
          <span className="block text-[11px] uppercase tracking-wider font-semibold text-[#5C5852] mb-1">ID (Slug)</span>
          <input type="text" value={id} data-testid="add-bundle-id" placeholder="z. B. komfort"
            onChange={(e) => setId(e.target.value)}
            className="w-full px-2 py-1.5 rounded border border-[#E5E0D2] text-[13px]" />
        </label>
        <label className="block">
          <span className="block text-[11px] uppercase tracking-wider font-semibold text-[#5C5852] mb-1">Name (DE)</span>
          <input type="text" value={nameDe} data-testid="add-bundle-name-de"
            onChange={(e) => setNameDe(e.target.value)}
            className="w-full px-2 py-1.5 rounded border border-[#E5E0D2] text-[13px]" />
        </label>
        <label className="block">
          <span className="block text-[11px] uppercase tracking-wider font-semibold text-[#5C5852] mb-1">Name (NL)</span>
          <input type="text" value={nameNl} data-testid="add-bundle-name-nl"
            onChange={(e) => setNameNl(e.target.value)}
            className="w-full px-2 py-1.5 rounded border border-[#E5E0D2] text-[13px]" />
        </label>
      </div>
      {error && <p className="text-[12px] text-[#C03A1A] inline-flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" /> {error}</p>}
      <button type="button" onClick={submit} data-testid="add-bundle-submit"
        className="inline-flex items-center gap-1 px-4 py-1.5 rounded-full bg-[#F26B1A] text-white text-[12.5px] font-semibold hover:bg-[#D85A0A] transition-colors">
        <Plus className="w-3.5 h-3.5" /> Bundle anlegen
      </button>
    </div>
  );
}

function BundlesTab({ data, onChange }) {
  const bundles = data?.bundles?.items ?? [];

  const setBundle = (idx, patch) => {
    const next = deepClone(data);
    next.bundles.items[idx] = { ...next.bundles.items[idx], ...patch };
    onChange(next);
  };

  const addBundle = (bundle) => {
    const next = deepClone(data);
    if (!next.bundles) next.bundles = { items: [] };
    if (!Array.isArray(next.bundles.items)) next.bundles.items = [];
    next.bundles.items.push(bundle);
    onChange(next);
  };

  const removeBundle = (idx) => {
    const b = bundles[idx];
    if (!window.confirm(`Bundle "${b.name?.de || b.id}" wirklich löschen?`)) return;
    const next = deepClone(data);
    next.bundles.items.splice(idx, 1);
    onChange(next);
  };

  const duplicateBundle = (idx) => {
    const src = bundles[idx];
    if (!src) return;
    // Auto-pick a free id: "{src.id}-copy", "-copy-2", "-copy-3", …
    const existing = new Set(bundles.map((b) => b.id));
    let candidate = `${src.id}-copy`;
    let n = 2;
    while (existing.has(candidate)) {
      candidate = `${src.id}-copy-${n++}`;
    }
    const copy = deepClone(src);
    copy.id = candidate;
    copy.name = {
      de: src.name?.de ? `${src.name.de} (Kopie)` : 'Kopie',
      nl: src.name?.nl ? `${src.name.nl} (kopie)` : 'Kopie',
    };
    copy.highlight = false; // Don't duplicate the "Beliebt" marker.
    const next = deepClone(data);
    next.bundles.items.splice(idx + 1, 0, copy); // insert right after source
    onChange(next);
  };

  const addBundleProduct = (idx) => {
    const next = deepClone(data);
    if (!Array.isArray(next.bundles.items[idx].products)) next.bundles.items[idx].products = [];
    next.bundles.items[idx].products.push({ category: '', slug: '', qty: 1 });
    onChange(next);
  };

  const updateBundleProduct = (idx, pIdx, prod) => {
    const next = deepClone(data);
    next.bundles.items[idx].products[pIdx] = prod;
    onChange(next);
  };

  const removeBundleProduct = (idx, pIdx) => {
    const next = deepClone(data);
    next.bundles.items[idx].products.splice(pIdx, 1);
    onChange(next);
  };

  return (
    <div className="space-y-4" data-testid="prices-tab-bundles">
      {bundles.map((b, i) => (
        <article key={b.id} data-testid={`bundle-${b.id}`} className="bg-white rounded-2xl border border-[#E5E0D2] p-5">
          <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
            <div>
              <p className="font-display text-[16px]">{b.name?.de || b.name?.nl || b.id}</p>
              <code className="text-[11px] text-[#5C5852]">{b.id}</code>
            </div>
            <div className="flex items-center gap-1 flex-wrap">
              <button type="button" onClick={() => duplicateBundle(i)}
                data-testid={`bundle-${b.id}-duplicate`}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11.5px] font-semibold text-[#5C5852] hover:bg-[#F26B1A]/10 hover:text-[#F26B1A] transition-colors">
                <Copy className="w-3.5 h-3.5" /> Duplizieren
              </button>
              <button type="button" onClick={() => removeBundle(i)}
                data-testid={`bundle-${b.id}-delete`}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11.5px] font-semibold text-[#5C5852] hover:bg-[#C03A1A]/10 hover:text-[#C03A1A] transition-colors">
                <Trash2 className="w-3.5 h-3.5" /> Bundle löschen
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-[11px] uppercase tracking-wider font-semibold text-[#5C5852] mb-1">Name (DE)</span>
              <input type="text" value={b.name?.de || ''}
                data-testid={`bundle-${b.id}-name-de`}
                onChange={(e) => setBundle(i, { name: { ...b.name, de: e.target.value } })}
                className="w-full px-2 py-1.5 rounded border border-[#E5E0D2] text-[13px]" />
            </label>
            <label className="block">
              <span className="block text-[11px] uppercase tracking-wider font-semibold text-[#5C5852] mb-1">Name (NL)</span>
              <input type="text" value={b.name?.nl || ''}
                data-testid={`bundle-${b.id}-name-nl`}
                onChange={(e) => setBundle(i, { name: { ...b.name, nl: e.target.value } })}
                className="w-full px-2 py-1.5 rounded border border-[#E5E0D2] text-[13px]" />
            </label>
            <label className="block">
              <span className="block text-[11px] uppercase tracking-wider font-semibold text-[#5C5852] mb-1">Tagline (DE)</span>
              <input type="text" value={b.tagline?.de || ''}
                data-testid={`bundle-${b.id}-tagline-de`}
                onChange={(e) => setBundle(i, { tagline: { ...b.tagline, de: e.target.value } })}
                className="w-full px-2 py-1.5 rounded border border-[#E5E0D2] text-[13px]" />
            </label>
            <label className="block">
              <span className="block text-[11px] uppercase tracking-wider font-semibold text-[#5C5852] mb-1">Tagline (NL)</span>
              <input type="text" value={b.tagline?.nl || ''}
                data-testid={`bundle-${b.id}-tagline-nl`}
                onChange={(e) => setBundle(i, { tagline: { ...b.tagline, nl: e.target.value } })}
                className="w-full px-2 py-1.5 rounded border border-[#E5E0D2] text-[13px]" />
            </label>
            <label className="block">
              <span className="block text-[11px] uppercase tracking-wider font-semibold text-[#5C5852] mb-1">Rabatt (%)</span>
              <input type="number" min={0} max={50} step={1} value={b.discount ?? 0}
                data-testid={`bundle-${b.id}-discount`}
                onChange={(e) => setBundle(i, { discount: toNumberOrNull(e.target.value) ?? 0 })}
                className="w-full px-2 py-1.5 rounded border border-[#E5E0D2] text-[13px] tabular-nums" />
            </label>
            <label className="block">
              <span className="block text-[11px] uppercase tracking-wider font-semibold text-[#5C5852] mb-1">Badge</span>
              <select value={b.badge || ''} data-testid={`bundle-${b.id}-badge`}
                onChange={(e) => setBundle(i, { badge: e.target.value || undefined })}
                className="w-full px-2 py-1.5 rounded border border-[#E5E0D2] text-[12px] bg-white">
                {BADGE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="block text-[11px] uppercase tracking-wider font-semibold text-[#5C5852] mb-1">Bestand</span>
              <input type="number" min={0} value={b.stockLeft ?? ''}
                placeholder="—"
                data-testid={`bundle-${b.id}-stock`}
                onChange={(e) => {
                  const v = toNumberOrNull(e.target.value);
                  setBundle(i, { stockLeft: v });
                }}
                className="w-full px-2 py-1.5 rounded border border-[#E5E0D2] text-[13px] tabular-nums" />
            </label>
            <div className="flex items-center gap-4 sm:pt-6">
              <label className="inline-flex items-center gap-2 text-[12.5px]">
                <input type="checkbox" checked={!!b.highlight}
                  data-testid={`bundle-${b.id}-highlight`}
                  onChange={(e) => setBundle(i, { highlight: e.target.checked })} />
                Beliebt
              </label>
              <label className="inline-flex items-center gap-2 text-[12.5px]">
                <input type="checkbox" checked={!!b.soldOut}
                  data-testid={`bundle-${b.id}-soldout`}
                  onChange={(e) => setBundle(i, { soldOut: e.target.checked })} />
                Ausverkauft
              </label>
            </div>
          </div>

          <div className="mt-5 pt-4 border-t border-[#E5E0D2]">
            <p className="text-[11.5px] uppercase tracking-wider font-semibold text-[#5C5852] mb-2">
              Produkte im Bundle
            </p>
            {(b.products || []).length === 0 && (
              <p className="text-[12px] text-[#5C5852] mb-2">Noch keine Produkte zugewiesen.</p>
            )}
            <ul className="space-y-2">
              {(b.products || []).map((p, pIdx) => (
                <li key={`${b.id}-p-${pIdx}-${p.category}-${p.slug}`}
                  data-testid={`bundle-${b.id}-product-${pIdx}`}
                  className="flex items-center justify-between gap-2 p-2 bg-[#FAF8F2] rounded-lg">
                  <ProductPicker data={data} value={p}
                    testId={`bundle-${b.id}-product-${pIdx}-picker`}
                    onChange={(np) => updateBundleProduct(i, pIdx, np)} />
                  <button type="button" onClick={() => removeBundleProduct(i, pIdx)}
                    data-testid={`bundle-${b.id}-product-${pIdx}-remove`}
                    title="Produkt entfernen"
                    className="inline-flex w-7 h-7 items-center justify-center rounded-full text-[#5C5852] hover:bg-[#C03A1A]/10 hover:text-[#C03A1A] transition-colors shrink-0">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ul>
            <button type="button" onClick={() => addBundleProduct(i)}
              data-testid={`bundle-${b.id}-add-product`}
              className="mt-3 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white border border-[#E5E0D2] text-[11.5px] font-semibold text-[#5C5852] hover:border-[#F26B1A] hover:text-[#F26B1A] transition-colors">
              <Plus className="w-3 h-3" /> Produkt hinzufügen
            </button>
          </div>
        </article>
      ))}

      <AddBundleForm existingIds={bundles.map((b) => b.id)} onAdd={addBundle} />
    </div>
  );
}


// ============================================================================
// Labels tab (unchanged from previous version)
// ============================================================================

function LabelsTab({ data, onChange }) {
  const labels = data?.bundles?.labels ?? {};
  const setLabel = (key, lang, value) => {
    const next = deepClone(data);
    if (!next.bundles.labels) next.bundles.labels = {};
    if (!next.bundles.labels[key]) next.bundles.labels[key] = {};
    next.bundles.labels[key][lang] = value;
    onChange(next);
  };
  const fields = [
    { key: 'whiteStandard', label: 'Hinweis "Weiß + Standard-Gewebe"' },
    { key: 'vatNote',       label: 'MwSt.-Hinweis' },
    { key: 'shippingNote',  label: 'Versand-Hinweis' },
  ];
  return (
    <div className="space-y-4" data-testid="prices-tab-labels">
      {fields.map((f) => (
        <article key={f.key} className="bg-white rounded-2xl border border-[#E5E0D2] p-5">
          <p className="text-[12.5px] uppercase tracking-wider font-semibold text-[#5C5852] mb-3">{f.label}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-[11px] uppercase tracking-wider font-semibold text-[#5C5852] mb-1">Deutsch</span>
              <textarea rows={2} value={labels[f.key]?.de || ''}
                data-testid={`label-${f.key}-de`}
                onChange={(e) => setLabel(f.key, 'de', e.target.value)}
                className="w-full px-2 py-1.5 rounded border border-[#E5E0D2] text-[13px] resize-none" />
            </label>
            <label className="block">
              <span className="block text-[11px] uppercase tracking-wider font-semibold text-[#5C5852] mb-1">Niederländisch</span>
              <textarea rows={2} value={labels[f.key]?.nl || ''}
                data-testid={`label-${f.key}-nl`}
                onChange={(e) => setLabel(f.key, 'nl', e.target.value)}
                className="w-full px-2 py-1.5 rounded border border-[#E5E0D2] text-[13px] resize-none" />
            </label>
          </div>
        </article>
      ))}
    </div>
  );
}


function RawJsonTab({ data, onChange }) {
  const [text, setText] = useState(() => JSON.stringify(data, null, 2));
  const [error, setError] = useState(null);
  const lastJsonRef = useRef(JSON.stringify(data));
  useEffect(() => {
    const ser = JSON.stringify(data);
    if (ser !== lastJsonRef.current) {
      lastJsonRef.current = ser;
      setText(JSON.stringify(data, null, 2));
      setError(null);
    }
  }, [data]);

  const onBlur = () => {
    try {
      const parsed = JSON.parse(text);
      if (!parsed || typeof parsed !== 'object') throw new Error('Wurzel muss ein Objekt sein.');
      setError(null);
      onChange(parsed);
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div data-testid="prices-tab-raw">
      <p className="text-[12px] text-[#5C5852] mb-2">
        Direkter Editor des kompletten <code>prices.json</code>. Beim Verlassen des Feldes wird validiert.
      </p>
      <textarea value={text} onChange={(e) => setText(e.target.value)} onBlur={onBlur}
        data-testid="prices-raw-textarea"
        spellCheck={false}
        className="w-full font-mono text-[12.5px] p-3 rounded-lg border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none"
        style={{ minHeight: '60vh' }} />
      {error && <p data-testid="prices-raw-err" className="mt-2 inline-flex items-start gap-2 text-[12.5px] text-[#C03A1A]"><AlertCircle className="w-4 h-4 mt-0.5" /> {error}</p>}
    </div>
  );
}


// ============================================================================
// Page shell
// ============================================================================

export default function AdminPricesPage() {
  const [data, setData] = useState(null);
  const [pristine, setPristine] = useState(null);
  const [tab, setTab] = useState('products');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data: doc } = await adminApi.get('/prices');
      setData(doc); setPristine(JSON.stringify(doc));
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const dirty = data && JSON.stringify(data) !== pristine;

  const save = async () => {
    setError(null); setBusy(true);
    try {
      const { data: saved_doc } = await adminApi.put('/admin/prices', data);
      setData(saved_doc); setPristine(JSON.stringify(saved_doc));
      setSaved(true); setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Speichern fehlgeschlagen.');
    } finally { setBusy(false); }
  };

  const revert = () => { if (window.confirm('Alle ungespeicherten Änderungen verwerfen?')) fetchData(); };

  const tabBody = useMemo(() => {
    if (!data) return null;
    switch (tab) {
      case 'products': return <ProductsTab data={data} onChange={setData} />;
      case 'bundles':  return <BundlesTab  data={data} onChange={setData} />;
      case 'labels':   return <LabelsTab   data={data} onChange={setData} />;
      case 'raw':      return <RawJsonTab  data={data} onChange={setData} />;
      default: return null;
    }
  }, [tab, data]);

  return (
    <div data-testid="admin-prices-page" className="w-full max-w-none">
      <div className="flex items-baseline justify-between gap-3 mb-6 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="inline-flex w-10 h-10 rounded-full bg-[#F26B1A]/10 text-[#F26B1A] items-center justify-center">
            <Tag className="w-5 h-5" />
          </span>
          <h1 className="font-display text-[22px] sm:text-[24px] lg:text-[28px] text-[#1F1D1A] leading-none">Preislisten</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {dirty && (
            <button type="button" onClick={revert} data-testid="admin-prices-revert"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#E5E0D2] text-[12px] font-semibold text-[#5C5852] hover:border-[#C03A1A] hover:text-[#C03A1A] transition-colors">
              <RefreshCcw className="w-3.5 h-3.5" /> Verwerfen
            </button>
          )}
          <button type="button" onClick={save} disabled={!dirty || busy} data-testid="admin-prices-save"
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[#F26B1A] text-white text-[12.5px] font-semibold hover:bg-[#D85A0A] disabled:opacity-40 transition-colors">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Speichern
          </button>
          {saved && <span className="inline-flex items-center gap-1.5 text-[#7FC242] text-[12.5px]"><CheckCircle2 className="w-4 h-4" /> Gespeichert</span>}
        </div>
      </div>

      {error && <p data-testid="admin-prices-err" className="mb-3 inline-flex items-start gap-2 text-[12.5px] text-[#C03A1A]"><AlertCircle className="w-4 h-4 mt-0.5" /> {error}</p>}

      <div className="bg-white rounded-xl border border-[#E5E0D2] p-1 inline-flex gap-1 mb-5 flex-wrap max-w-full overflow-x-auto">
        {TABS.map((t) => {
          const Icon = t.Icon;
          return (
            <button key={t.id} type="button" onClick={() => setTab(t.id)}
              data-testid={`prices-tab-${t.id}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12.5px] font-semibold transition-colors whitespace-nowrap ${
                tab === t.id ? 'bg-[#F26B1A] text-white' : 'text-[#5C5852] hover:bg-[#FAF8F2]'
              }`}>
              <Icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="p-10 text-center text-[#5C5852]"><Loader2 className="inline w-5 h-5 animate-spin" /></div>
      ) : tabBody}
    </div>
  );
}
